import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Box } from '@iheartjane/reefer';
import type { ReservationMode } from '@iheartjane/types';
import { useEffect, useRef, useState } from 'react';

import { useApplyLocalPromoCode } from '@jane/monolith/components/cart/cartFacade/useApplyLocalPromoCode';
import { useUrlPromoCode } from '@jane/monolith/components/cart/cartFacade/useCartEntryParams';
import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import {
  analyzeSpecialsSuccess,
  selectCart,
} from '@jane/monolith/customer/redux/cart';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { useValidatePromoCode } from '@jane/shared/data-access';

import { SubmittableTextInput } from './SubmittableTextInput';

interface PromoCodesProps {
  headless?: boolean;
  reservationMode: ReservationMode;
  userGroupSpecialId?: string | number;
}

export const PromoCodes = ({
  headless,
  reservationMode,
  userGroupSpecialId,
}: PromoCodesProps) => {
  const [promoCode, setPromoCode] = useState<string | undefined>();
  const useV3Cart = !!useFlag(FLAGS.c3UseV3Cart);
  const cartState = useCustomerSelector(selectCart);
  const dispatch = useCustomerDispatch();
  const { consumeUrlPromoCode, urlPromoCode } = useUrlPromoCode();
  const v3Promo = useApplyLocalPromoCode(useV3Cart);
  const hasAutoSubmitted = useRef(false);

  // Flag-on the v3 mutation validates, so the v1 request stays off.
  const { data: promoCodeData, refetch } = useValidatePromoCode(
    {
      promoCode,
      reservationMode,
      userGroupSpecialId,
      uuid: cartState.cart.uuid,
      // Coerce to resolve the mismatches in optionality between the Address implementations
      // in @jane/shared/models and @jane/shared/types
      deliveryAddress: cartState.deliveryAddress
        ? cartState.deliveryAddress
        : undefined,
    },
    { enabled: !useV3Cart }
  );

  //dispatches will be updated when cart redux store is removed
  useEffect(() => {
    if (!useV3Cart && urlPromoCode) {
      setPromoCode(urlPromoCode);
    }
  }, []);

  // Waits for a cart, then submits `urlPromoCode` once and consumes it.
  useEffect(() => {
    if (!useV3Cart || !urlPromoCode || hasAutoSubmitted.current) return;

    if (v3Promo.apply(urlPromoCode)) {
      hasAutoSubmitted.current = true;
      consumeUrlPromoCode();
    }
  }, [useV3Cart, urlPromoCode, v3Promo.cartUuid]);

  useEffect(() => {
    if (promoCodeData?.validated && promoCodeData?.isValid) {
      dispatch(
        analyzeSpecialsSuccess({
          cart: promoCodeData.cart,
          userGroupSpecialId,
        })
      );
    }
  }, [promoCodeData]);

  const updatePromoCode = (value?: string) => {
    if (useV3Cart) {
      if (!value) {
        v3Promo.reset();
      } else if (!v3Promo.apply(value)) {
        // No cart yet: nothing sent, so `urlPromoCode` isn't spent either.
        return;
      }
      consumeUrlPromoCode();
      return;
    }

    if (value === promoCode) {
      refetch();
    } else {
      setPromoCode(value);
    }
    consumeUrlPromoCode();
  };

  const validated = useV3Cart
    ? v3Promo.isSuccess || v3Promo.isError
    : (promoCodeData?.validated ?? false);
  const isValid = useV3Cart ? v3Promo.isSuccess : promoCodeData?.isValid;

  return (
    <Box pl={headless ? 0 : 40}>
      <SubmittableTextInput
        name="promo_code"
        placeholder="Enter code here"
        submitLabel="Apply"
        onReset={() => updatePromoCode(undefined)}
        onSubmit={(value) => updatePromoCode(value)}
        validated={validated}
        hasValidationError={!isValid}
        validationMessage={
          isValid
            ? 'Success! Code will be applied if cart is eligible.'
            : 'Not Valid'
        }
      />
    </Box>
  );
};
